import { Plus, Trash2, Users } from 'lucide-react'
import { type FormEvent, type ReactNode, useState } from 'react'
import { Checkbox } from '@/components/ui/Checkbox'
import { EmptyState } from '@/components/ui/EmptyState'
import { ErrorState } from '@/components/ui/ErrorState'
import { InfoTooltip } from '@/components/ui/InfoTooltip'
import { Modal } from '@/components/ui/Modal'
import { PageHeader } from '@/components/ui/PageHeader'
import { Select, type SelectOption } from '@/components/ui/Select'
import { Skeleton } from '@/components/ui/Skeleton'
import { Uitklapper } from '@/components/ui/Uitklapper'
import { Veld } from '@/components/ui/Veld'
import { nettoFte, urenPerSemester } from '@/lib/aanstelling'
import { formatDatum, formatUren } from '@/lib/format'
import { useMe } from '@/lib/me-api'
import {
  useMaakAanstelling,
  useMaakMedewerker,
  useMedewerkers,
  useVerwijderAanstelling,
  useWijzigAanstelling,
  useWijzigMedewerker,
} from '@/lib/medewerker-api'
import type {
  Aanstelling,
  AanstellingInvoer,
  Medewerker,
  Sectie,
} from '@/lib/medewerker-types'
import {
  useMaakSemester,
  useSemesterDetail,
  useVerwijderSemester,
  useWijzigSemester,
  useWijzigSleutels,
} from '@/lib/semester-api'
import { useSemesterKeuze } from '@/lib/semester-context'
import type { Semester, SemesterStatus, Sleutels } from '@/lib/semester-types'

const GEEN_SECTIE = 'geen'

const STATUS_OPTIES: SelectOption[] = [
  { value: 'concept', label: 'Concept' },
  { value: 'actief', label: 'Actief' },
  { value: 'afgerond', label: 'Afgerond' },
]

const SECTIE_OPTIES: SelectOption[] = [
  { value: GEEN_SECTIE, label: 'Geen sectie' },
  { value: 'nt2', label: 'NT2' },
  { value: 'engels', label: 'Engels' },
  { value: 'wiskunde', label: 'Wiskunde' },
  { value: 'coaches', label: 'Coaches' },
  { value: 'bedrijfsbureau', label: 'Bedrijfsbureau' },
  { value: 'maatwerkvakken', label: 'Maatwerkvakken' },
]

const SLEUTELVELDEN: Array<{ sleutel: keyof Sleutels; label: string; hint: string; stap: string }> =
  [
    { sleutel: 'urenPerDagdeel', label: 'Uren per dagdeel', hint: 'Contacttijd', stap: '0.25' },
    { sleutel: 'opslagFactor', label: 'Opslagfactor', hint: 'Voorbereiding', stap: '0.05' },
    {
      sleutel: 'mentoraatUrenPerLesweek',
      label: 'Mentoraat per lesweek',
      hint: 'Uren',
      stap: '0.25',
    },
    { sleutel: 'coachingUrenPerWeek', label: 'Coaching per week', hint: 'Uren', stap: '0.25' },
    { sleutel: 'fteUrenPerJaar', label: 'Uren per fte per jaar', hint: 'Norm', stap: '1' },
    { sleutel: 'balansMargePercentage', label: 'Marge', hint: 'Procent', stap: '0.5' },
  ]

export function InstellingenPage() {
  const { semester, semesterId, semesters, isLoading: semesterLaadt, isError } = useSemesterKeuze()
  const detail = useSemesterDetail(semesterId)
  const me = useMe()

  return (
    <div className="space-y-6">
      <PageHeader
        title="Instellingen"
        description="Semesters, rekensleutels, medewerkers en hun aanstellingen."
        actions={
          me.data ? (
            <span className="inline-flex items-center rounded-md bg-fill-1 px-2 py-1 text-xs font-medium text-ink-soft ring-1 ring-inset ring-hairline">
              {me.data.user.naam} · {me.data.user.rol}
            </span>
          ) : null
        }
      />

      <SemestersSectie semesters={semesters} laadt={semesterLaadt} isError={isError} />

      <SleutelsSectie
        key={semesterId}
        semesterId={semesterId}
        semester={semester}
        sleutels={detail.data?.sleutels ?? null}
        laadt={semesterLaadt || detail.isLoading}
        error={detail.error}
        onOpnieuw={() => detail.refetch()}
      />

      <MedewerkersSectie semester={semester} sleutels={detail.data?.sleutels ?? null} />
    </div>
  )
}

interface SemestersSectieProps {
  semesters: Semester[]
  laadt: boolean
  isError: boolean
}

function SemestersSectie({ semesters, laadt, isError }: SemestersSectieProps) {
  const wijzig = useWijzigSemester()
  const verwijder = useVerwijderSemester()
  const maak = useMaakSemester()
  const [open, setOpen] = useState(false)

  return (
    <section className="overflow-hidden rounded-card bg-surface shadow-card">
      <Sectiekop
        titel="Semesters"
        uitleg="De periode bepaalt welke weken meetellen in de berekening."
        actie={
          <button type="button" className="btn-secondary" onClick={() => setOpen(true)}>
            <Plus className="h-4 w-4" />
            Semester toevoegen
          </button>
        }
      />

      {isError ? (
        <ErrorState compact />
      ) : laadt ? (
        <div className="space-y-2 p-5">
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-10 w-full" />
        </div>
      ) : semesters.length === 0 ? (
        <EmptyState
          title="Nog geen semesters"
          description="Maak een semester aan om de planning te kunnen berekenen."
          className="m-5 border-0 bg-transparent py-10"
        />
      ) : (
        <>
          <div className="hidden grid-cols-[minmax(0,1fr)_9rem_9rem_9rem_2rem] gap-3 px-5 py-2.5 lg:grid">
            <span className="label-caps">Semester</span>
            <span className="label-caps">Start</span>
            <span className="label-caps">Eind</span>
            <span className="label-caps">Status</span>
            <span />
          </div>

          <ul className="divide-y divide-hairline border-t border-hairline">
            {semesters.map((semester) => (
              <li
                key={semester.id}
                className="grid grid-cols-2 gap-3 px-5 py-3 lg:grid-cols-[minmax(0,1fr)_9rem_9rem_9rem_2rem] lg:items-center"
              >
                <span className="col-span-2 min-w-0 lg:col-span-1">
                  <span className="block truncate text-sm text-ink">{semester.naam}</span>
                  <span className="block text-xs text-ink-faint">
                    {semester.jaar} · periode {semester.volgnummer}
                  </span>
                </span>

                <input
                  type="date"
                  aria-label="Startdatum"
                  className="field h-9"
                  defaultValue={semester.startDatum}
                  onBlur={(gebeurtenis) => {
                    const waarde = gebeurtenis.target.value
                    if (waarde && waarde !== semester.startDatum) {
                      wijzig.mutate({ semesterId: semester.id, input: { startDatum: waarde } })
                    }
                  }}
                />
                <input
                  type="date"
                  aria-label="Einddatum"
                  className="field h-9"
                  defaultValue={semester.eindDatum}
                  onBlur={(gebeurtenis) => {
                    const waarde = gebeurtenis.target.value
                    if (waarde && waarde !== semester.eindDatum) {
                      wijzig.mutate({ semesterId: semester.id, input: { eindDatum: waarde } })
                    }
                  }}
                />

                <Select
                  value={semester.status}
                  onValueChange={(waarde) =>
                    wijzig.mutate({
                      semesterId: semester.id,
                      input: { status: waarde as SemesterStatus },
                    })
                  }
                  options={STATUS_OPTIES}
                  className="w-full"
                />

                <button
                  type="button"
                  className="btn-ghost h-8 w-8 justify-self-end p-0"
                  aria-label={`${semester.naam} verwijderen`}
                  onClick={() => verwijder.mutate(semester.id)}
                >
                  <Trash2 className="h-4 w-4" strokeWidth={1.85} />
                </button>
              </li>
            ))}
          </ul>
        </>
      )}

      <NieuwSemesterModal
        open={open}
        onOpenChange={setOpen}
        bezig={maak.isPending}
        onBewaar={(input) => maak.mutate(input, { onSuccess: () => setOpen(false) })}
      />
    </section>
  )
}

interface SleutelsSectieProps {
  semesterId: string
  semester: Semester | null
  sleutels: Sleutels | null
  laadt: boolean
  error: unknown
  onOpnieuw: () => void
}

function SleutelsSectie({
  semesterId,
  semester,
  sleutels,
  laadt,
  error,
  onOpnieuw,
}: SleutelsSectieProps) {
  const wijzig = useWijzigSleutels(semesterId)
  const [concept, setConcept] = useState<Record<string, string> | null>(null)

  const waarden =
    concept ??
    (sleutels
      ? Object.fromEntries(
          SLEUTELVELDEN.map((veld) => [veld.sleutel, String(sleutels[veld.sleutel])]),
        )
      : null)

  function verstuur(gebeurtenis: FormEvent) {
    gebeurtenis.preventDefault()
    if (!waarden) return

    const invoer: Partial<Sleutels> = {}
    for (const veld of SLEUTELVELDEN) {
      const getal = Number(String(waarden[veld.sleutel]).replace(',', '.'))
      if (!Number.isNaN(getal) && getal >= 0) invoer[veld.sleutel] = getal
    }
    wijzig.mutate(invoer, { onSuccess: () => setConcept(null) })
  }

  return (
    <section className="overflow-hidden rounded-card bg-surface shadow-card">
      <Sectiekop
        titel="Rekensleutels"
        uitleg={
          semester
            ? `Gelden voor ${semester.naam}. Elke wijziging herrekent het hele semester.`
            : 'Gelden voor het gekozen semester.'
        }
      />

      {error ? (
        <ErrorState error={error} onRetry={onOpnieuw} compact />
      ) : laadt || !waarden ? (
        <div className="grid grid-cols-2 gap-4 p-5 lg:grid-cols-4">
          {Array.from({ length: 7 }).map((_, index) => (
            <Skeleton key={index} className="h-16 w-full" />
          ))}
        </div>
      ) : (
        <form onSubmit={verstuur} className="p-5">
          <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
            {SLEUTELVELDEN.map((veld) => (
              <Veld key={veld.sleutel} label={veld.label} hint={veld.hint}>
                <input
                  className="field num"
                  type="number"
                  step={veld.stap}
                  min={0}
                  value={waarden[veld.sleutel]}
                  onChange={(gebeurtenis) =>
                    setConcept({ ...waarden, [veld.sleutel]: gebeurtenis.target.value })
                  }
                />
              </Veld>
            ))}
          </div>

          <div className="mt-5 flex items-center gap-2">
            <button type="submit" className="btn-primary" disabled={wijzig.isPending || !concept}>
              Sleutels opslaan
            </button>
            {concept ? (
              <button type="button" className="btn-ghost" onClick={() => setConcept(null)}>
                Terugzetten
              </button>
            ) : null}
          </div>
        </form>
      )}
    </section>
  )
}

interface MedewerkersSectieProps {
  semester: Semester | null
  sleutels: Sleutels | null
}

function MedewerkersSectie({ semester, sleutels }: MedewerkersSectieProps) {
  const medewerkers = useMedewerkers(false)
  const maak = useMaakMedewerker()
  const [open, setOpen] = useState(false)

  return (
    <section className="overflow-hidden rounded-card bg-surface shadow-card">
      <Sectiekop
        titel="Medewerkers en aanstellingen"
        uitleg="De netto fte bepaalt hoeveel uren iemand in dit semester beschikbaar is."
        actie={
          <button type="button" className="btn-secondary" onClick={() => setOpen(true)}>
            <Plus className="h-4 w-4" />
            Medewerker toevoegen
          </button>
        }
      />

      {medewerkers.isError ? (
        <ErrorState error={medewerkers.error} onRetry={() => medewerkers.refetch()} compact />
      ) : medewerkers.isLoading ? (
        <div className="space-y-2 p-5">
          {Array.from({ length: 5 }).map((_, index) => (
            <Skeleton key={index} className="h-12 w-full" />
          ))}
        </div>
      ) : !medewerkers.data || medewerkers.data.length === 0 ? (
        <EmptyState
          icon={Users}
          title="Nog geen medewerkers"
          description="Voeg de mensen toe die lessen of taken oppakken."
          className="m-5 border-0 bg-transparent py-10"
        />
      ) : (
        <ul className="divide-y divide-hairline border-t border-hairline">
          {medewerkers.data.map((medewerker) => (
            <MedewerkerKaart
              key={medewerker.id}
              medewerker={medewerker}
              semester={semester}
              sleutels={sleutels}
            />
          ))}
        </ul>
      )}

      <NieuweMedewerkerModal
        open={open}
        onOpenChange={setOpen}
        bezig={maak.isPending}
        onBewaar={(input) => maak.mutate(input, { onSuccess: () => setOpen(false) })}
      />
    </section>
  )
}

interface MedewerkerKaartProps {
  medewerker: Medewerker
  semester: Semester | null
  sleutels: Sleutels | null
}

function MedewerkerKaart({ medewerker, semester, sleutels }: MedewerkerKaartProps) {
  const wijzig = useWijzigMedewerker()
  const maakAanstelling = useMaakAanstelling()
  const [open, setOpen] = useState(false)
  const [nieuw, setNieuw] = useState(false)
  const [naam, setNaam] = useState(medewerker.naam)
  const [email, setEmail] = useState(medewerker.email ?? '')

  return (
    <li>
      <Uitklapper
        open={open}
        onOpenChange={setOpen}
        label={open ? 'Aanstellingen verbergen' : 'Aanstellingen tonen'}
        paneelClassName="border-t border-hairline bg-fill-1 px-5 py-4"
        kop={(knop) => (
          <div className="grid grid-cols-2 gap-3 px-5 py-3 lg:grid-cols-[2rem_minmax(0,1fr)_minmax(0,1fr)_10rem_6rem] lg:items-center">
            {knop}

            <input
              className="field h-9"
              aria-label="Naam"
              value={naam}
              onChange={(gebeurtenis) => setNaam(gebeurtenis.target.value)}
              onBlur={() => {
                const schoon = naam.trim()
                if (schoon !== '' && schoon !== medewerker.naam) {
                  wijzig.mutate({ medewerkerId: medewerker.id, input: { naam: schoon } })
                }
              }}
            />

            <input
              className="field h-9"
              aria-label="E-mail"
              placeholder="E-mail"
              value={email}
              onChange={(gebeurtenis) => setEmail(gebeurtenis.target.value)}
              onBlur={() => {
                const schoon = email.trim()
                if (schoon !== (medewerker.email ?? '')) {
                  wijzig.mutate({
                    medewerkerId: medewerker.id,
                    input: { email: schoon === '' ? null : schoon },
                  })
                }
              }}
            />

            <Select
              value={medewerker.sectie ?? GEEN_SECTIE}
              onValueChange={(waarde) =>
                wijzig.mutate({
                  medewerkerId: medewerker.id,
                  input: { sectie: waarde === GEEN_SECTIE ? null : (waarde as Sectie) },
                })
              }
              options={SECTIE_OPTIES}
              className="w-full"
            />

            <Checkbox
              checked={medewerker.actief}
              onChange={(aan) =>
                wijzig.mutate({ medewerkerId: medewerker.id, input: { actief: aan } })
              }
              label="Actief"
            />
          </div>
        )}
      >
        {medewerker.aanstellingen.length === 0 ? (
          <p className="text-sm text-ink-faint">
            Nog geen aanstelling. Zonder aanstelling blijft de balans onbekend.
          </p>
        ) : (
          <>
            <div className="hidden grid-cols-[8.5rem_8.5rem_5rem_5rem_5rem_minmax(0,1fr)_2rem] gap-3 px-4 pb-1.5 lg:grid">
              <span className="label-caps">Van</span>
              <span className="label-caps">Tot</span>
              <span className="label-caps inline-flex items-center gap-1">
                Bruto
                <InfoTooltip>Wat er op papier staat: de contractomvang in fte.</InfoTooltip>
              </span>
              <span className="label-caps inline-flex items-center gap-1">
                Detach.
                <InfoTooltip>
                  Detacheringsfte: het deel dat elders wordt ingezet en dus niet beschikbaar is
                  voor dit team.
                </InfoTooltip>
              </span>
              <span className="label-caps inline-flex items-center gap-1">
                Wtv
                <InfoTooltip>
                  Wtv-fte: werktijdvermindering, de seniorenregeling. Beide gaan van de bruto fte
                  af.
                </InfoTooltip>
              </span>
              <span className="label-caps inline-flex items-center gap-1">
                Netto
                <InfoTooltip>
                  Bruto min detachering min wtv, met een ondergrens van nul. Dit bepaalt hoeveel
                  uren iemand dit semester beschikbaar is.
                </InfoTooltip>
              </span>
              <span />
            </div>

            <ul className="space-y-2">
              {medewerker.aanstellingen.map((aanstelling) => (
                <AanstellingRegel
                  key={aanstelling.id}
                  aanstelling={aanstelling}
                  semester={semester}
                  sleutels={sleutels}
                />
              ))}
            </ul>
          </>
        )}

        <button type="button" className="btn-secondary mt-3" onClick={() => setNieuw(true)}>
          <Plus className="h-4 w-4" />
          Aanstelling toevoegen
        </button>

        <NieuweAanstellingModal
          open={nieuw}
          onOpenChange={setNieuw}
          naam={medewerker.naam}
          standaardVan={semester?.startDatum ?? ''}
          bezig={maakAanstelling.isPending}
          onBewaar={(input) =>
            maakAanstelling.mutate(
              { medewerkerId: medewerker.id, input },
              { onSuccess: () => setNieuw(false) },
            )
          }
        />
      </Uitklapper>
    </li>
  )
}

interface AanstellingRegelProps {
  aanstelling: Aanstelling
  semester: Semester | null
  sleutels: Sleutels | null
}

function AanstellingRegel({ aanstelling, semester, sleutels }: AanstellingRegelProps) {
  const wijzig = useWijzigAanstelling()
  const verwijder = useVerwijderAanstelling()

  const uren = semester ? urenPerSemester(aanstelling, semester, sleutels) : null

  function zet(input: Partial<AanstellingInvoer>) {
    wijzig.mutate({ aanstellingId: aanstelling.id, input })
  }

  return (
    <li className="grid grid-cols-2 gap-3 rounded-lg bg-surface px-4 py-3 shadow-card lg:grid-cols-[8.5rem_8.5rem_5rem_5rem_5rem_minmax(0,1fr)_2rem] lg:items-center">
      <input
        type="date"
        aria-label="Van"
        className="field h-9"
        defaultValue={aanstelling.geldigVan}
        onBlur={(gebeurtenis) => {
          const waarde = gebeurtenis.target.value
          if (waarde && waarde !== aanstelling.geldigVan) zet({ geldigVan: waarde })
        }}
      />

      <input
        type="date"
        aria-label="Tot"
        className="field h-9"
        defaultValue={aanstelling.geldigTot ?? ''}
        onBlur={(gebeurtenis) => {
          const waarde = gebeurtenis.target.value
          if (waarde !== (aanstelling.geldigTot ?? '')) zet({ geldigTot: waarde || null })
        }}
      />

      <FteVeld
        label="Bruto"
        waarde={aanstelling.brutoFte}
        onBewaar={(waarde) => zet({ brutoFte: waarde })}
      />
      <FteVeld
        label="Detach."
        waarde={aanstelling.detacheringFte}
        onBewaar={(waarde) => zet({ detacheringFte: waarde })}
      />
      <FteVeld
        label="Wtv"
        waarde={aanstelling.wtvFte}
        onBewaar={(waarde) => zet({ wtvFte: waarde })}
      />

      <span className="col-span-2 text-sm text-ink-soft lg:col-span-1">
        <span className="num font-medium text-ink">
          {nettoFte(aanstelling.brutoFte, aanstelling.detacheringFte, aanstelling.wtvFte).toLocaleString('nl-NL')}
        </span>
        <span className="text-ink-faint"> netto fte</span>
        {uren !== null ? (
          <span className="text-ink-faint">
            {' · '}
            {formatUren(uren)} u in {semester?.naam ?? 'dit semester'}
          </span>
        ) : null}
        {aanstelling.toelichting ? (
          <span className="block text-xs text-ink-faint">{aanstelling.toelichting}</span>
        ) : null}
      </span>

      <button
        type="button"
        className="btn-ghost h-8 w-8 justify-self-end p-0"
        aria-label={`Aanstelling vanaf ${formatDatum(aanstelling.geldigVan)} verwijderen`}
        onClick={() => verwijder.mutate(aanstelling.id)}
      >
        <Trash2 className="h-4 w-4" strokeWidth={1.85} />
      </button>
    </li>
  )
}

interface FteVeldProps {
  label: string
  waarde: number
  onBewaar: (waarde: number) => void
}

function FteVeld({ label, waarde, onBewaar }: FteVeldProps) {
  return (
    <input
      type="number"
      step="0.01"
      min={0}
      aria-label={label}
      placeholder={label}
      className="field num h-9"
      defaultValue={waarde}
      onBlur={(gebeurtenis) => {
        const nieuw = Number(gebeurtenis.target.value.replace(',', '.'))
        if (Number.isNaN(nieuw) || nieuw < 0) {
          gebeurtenis.target.value = String(waarde)
          return
        }
        if (nieuw !== waarde) onBewaar(nieuw)
      }}
    />
  )
}

function Sectiekop({
  titel,
  uitleg,
  actie,
}: {
  titel: string
  uitleg: string
  actie?: ReactNode
}) {
  return (
    <div className="flex flex-wrap items-baseline justify-between gap-3 border-b border-hairline px-5 py-4">
      <div className="min-w-0">
        <h2 className="text-sm font-semibold text-ink">{titel}</h2>
        <p className="mt-0.5 text-xs text-ink-faint">{uitleg}</p>
      </div>
      {actie}
    </div>
  )
}

interface NieuwSemesterModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  bezig: boolean
  onBewaar: (input: {
    naam: string
    jaar: number
    volgnummer: number
    startDatum: string
    eindDatum: string
  }) => void
}

function NieuwSemesterModal({ open, onOpenChange, bezig, onBewaar }: NieuwSemesterModalProps) {
  const [naam, setNaam] = useState('')
  const [jaar, setJaar] = useState('')
  const [volgnummer, setVolgnummer] = useState('1')
  const [startDatum, setStartDatum] = useState('')
  const [eindDatum, setEindDatum] = useState('')

  function verstuur(gebeurtenis: FormEvent) {
    gebeurtenis.preventDefault()
    if (naam.trim() === '' || startDatum === '' || eindDatum === '') return

    onBewaar({
      naam: naam.trim(),
      jaar: Number(jaar),
      volgnummer: Number(volgnummer),
      startDatum,
      eindDatum,
    })
  }

  return (
    <Modal
      open={open}
      onOpenChange={onOpenChange}
      title="Semester toevoegen"
      description="Het nieuwe semester begint met een kopie van de laatste sleutels."
      footer={
        <>
          <button type="button" className="btn-ghost" onClick={() => onOpenChange(false)}>
            Annuleren
          </button>
          <button type="submit" form="nieuw-semester" className="btn-primary" disabled={bezig}>
            Toevoegen
          </button>
        </>
      }
    >
      <form id="nieuw-semester" onSubmit={verstuur} className="space-y-4">
        <Veld label="Naam">
          <input
            className="field"
            value={naam}
            onChange={(gebeurtenis) => setNaam(gebeurtenis.target.value)}
            placeholder="Semester 1 2027"
            required
          />
        </Veld>

        <div className="grid grid-cols-2 gap-3">
          <Veld label="Jaar">
            <input
              className="field num"
              type="number"
              value={jaar}
              onChange={(gebeurtenis) => setJaar(gebeurtenis.target.value)}
              placeholder="2027"
              required
            />
          </Veld>
          <Veld label="Volgnummer">
            <input
              className="field num"
              type="number"
              min={1}
              value={volgnummer}
              onChange={(gebeurtenis) => setVolgnummer(gebeurtenis.target.value)}
              required
            />
          </Veld>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <Veld label="Startdatum">
            <input
              className="field"
              type="date"
              value={startDatum}
              onChange={(gebeurtenis) => setStartDatum(gebeurtenis.target.value)}
              required
            />
          </Veld>
          <Veld label="Einddatum">
            <input
              className="field"
              type="date"
              value={eindDatum}
              onChange={(gebeurtenis) => setEindDatum(gebeurtenis.target.value)}
              required
            />
          </Veld>
        </div>
      </form>
    </Modal>
  )
}

interface NieuweMedewerkerModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  bezig: boolean
  onBewaar: (input: { naam: string; email: string | null; sectie: Sectie | null }) => void
}

function NieuweMedewerkerModal({
  open,
  onOpenChange,
  bezig,
  onBewaar,
}: NieuweMedewerkerModalProps) {
  const [naam, setNaam] = useState('')
  const [email, setEmail] = useState('')
  const [sectie, setSectie] = useState(GEEN_SECTIE)

  function verstuur(gebeurtenis: FormEvent) {
    gebeurtenis.preventDefault()
    if (naam.trim() === '') return

    onBewaar({
      naam: naam.trim(),
      email: email.trim() || null,
      sectie: sectie === GEEN_SECTIE ? null : (sectie as Sectie),
    })
  }

  return (
    <Modal
      open={open}
      onOpenChange={(volgende) => {
        onOpenChange(volgende)
        if (!volgende) {
          setNaam('')
          setEmail('')
          setSectie(GEEN_SECTIE)
        }
      }}
      title="Medewerker toevoegen"
      description="De aanstelling leg je daarna vast bij de medewerker."
      footer={
        <>
          <button type="button" className="btn-ghost" onClick={() => onOpenChange(false)}>
            Annuleren
          </button>
          <button type="submit" form="nieuwe-medewerker" className="btn-primary" disabled={bezig}>
            Toevoegen
          </button>
        </>
      }
    >
      <form id="nieuwe-medewerker" onSubmit={verstuur} className="space-y-4">
        <Veld label="Naam">
          <input
            className="field"
            value={naam}
            onChange={(gebeurtenis) => setNaam(gebeurtenis.target.value)}
            required
          />
        </Veld>
        <Veld label="E-mail">
          <input
            className="field"
            type="email"
            value={email}
            onChange={(gebeurtenis) => setEmail(gebeurtenis.target.value)}
          />
        </Veld>
        <Veld label="Sectie">
          <Select value={sectie} onValueChange={setSectie} options={SECTIE_OPTIES} />
        </Veld>
      </form>
    </Modal>
  )
}

interface NieuweAanstellingModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  naam: string
  standaardVan: string
  bezig: boolean
  onBewaar: (input: AanstellingInvoer) => void
}

function NieuweAanstellingModal({
  open,
  onOpenChange,
  naam,
  standaardVan,
  bezig,
  onBewaar,
}: NieuweAanstellingModalProps) {
  const [geldigVan, setGeldigVan] = useState(standaardVan)
  const [geldigTot, setGeldigTot] = useState('')
  const [bruto, setBruto] = useState('')
  const [detachering, setDetachering] = useState('0')
  const [wtv, setWtv] = useState('0')
  const [toelichting, setToelichting] = useState('')

  function verstuur(gebeurtenis: FormEvent) {
    gebeurtenis.preventDefault()
    if (geldigVan === '' || bruto === '') return

    onBewaar({
      geldigVan,
      geldigTot: geldigTot || null,
      brutoFte: Number(bruto.replace(',', '.')),
      detacheringFte: Number(detachering.replace(',', '.')) || 0,
      wtvFte: Number(wtv.replace(',', '.')) || 0,
      toelichting: toelichting.trim() || null,
    })
  }

  return (
    <Modal
      open={open}
      onOpenChange={onOpenChange}
      title={`Aanstelling voor ${naam}`}
      description="Netto fte is bruto min detachering min wtv."
      footer={
        <>
          <button type="button" className="btn-ghost" onClick={() => onOpenChange(false)}>
            Annuleren
          </button>
          <button type="submit" form="nieuwe-aanstelling" className="btn-primary" disabled={bezig}>
            Toevoegen
          </button>
        </>
      }
    >
      <form id="nieuwe-aanstelling" onSubmit={verstuur} className="space-y-4">
        <div className="grid grid-cols-2 gap-3">
          <Veld label="Geldig van">
            <input
              className="field"
              type="date"
              value={geldigVan}
              onChange={(gebeurtenis) => setGeldigVan(gebeurtenis.target.value)}
              required
            />
          </Veld>
          <Veld label="Geldig tot" hint="Leeg is doorlopend">
            <input
              className="field"
              type="date"
              value={geldigTot}
              onChange={(gebeurtenis) => setGeldigTot(gebeurtenis.target.value)}
            />
          </Veld>
        </div>

        <div className="grid grid-cols-3 gap-3">
          <Veld label="Bruto fte">
            <input
              className="field num"
              type="number"
              step="0.01"
              min={0}
              value={bruto}
              onChange={(gebeurtenis) => setBruto(gebeurtenis.target.value)}
              required
            />
          </Veld>
          <Veld label="Detachering">
            <input
              className="field num"
              type="number"
              step="0.01"
              min={0}
              value={detachering}
              onChange={(gebeurtenis) => setDetachering(gebeurtenis.target.value)}
            />
          </Veld>
          <Veld label="Wtv">
            <input
              className="field num"
              type="number"
              step="0.01"
              min={0}
              value={wtv}
              onChange={(gebeurtenis) => setWtv(gebeurtenis.target.value)}
            />
          </Veld>
        </div>

        <Veld label="Toelichting">
          <input
            className="field"
            value={toelichting}
            onChange={(gebeurtenis) => setToelichting(gebeurtenis.target.value)}
            placeholder="Ingeleend, ouderschapsverlof"
          />
        </Veld>
      </form>
    </Modal>
  )
}
